import { useState } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { MapPin, Info } from "lucide-react";
import { photoSrc } from "../lib/api";

export default function ProfileCard({ profile, onSwipe, drag = false }) {
  const [photoIdx, setPhotoIdx] = useState(0);
  const [showBio, setShowBio] = useState(false);
  const photos = profile.photos?.length ? profile.photos : [{ url: "" }];

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-220, 220], [-16, 16]);
  const likeOpacity = useTransform(x, [30, 130], [0, 1]);
  const nopeOpacity = useTransform(x, [-130, -30], [1, 0]);

  const handleDragEnd = (_e, info) => {
    if (info.offset.x > 120) onSwipe("like");
    else if (info.offset.x < -120) onSwipe("pass");
  };

  const tapPhoto = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = e.clientX - rect.left < rect.width / 2;
    setPhotoIdx((i) => {
      if (left) return Math.max(0, i - 1);
      return Math.min(photos.length - 1, i + 1);
    });
  };

  return (
    <motion.div
      data-testid={drag ? "swipe-current-profile-card" : undefined}
      className="absolute inset-0 rounded-[1.75rem] overflow-hidden bg-[#1D1829] border border-white/10 shadow-2xl select-none"
      style={drag ? { x, rotate } : {}}
      drag={drag ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.6}
      onDragEnd={drag ? handleDragEnd : undefined}
      whileTap={drag ? { cursor: "grabbing" } : {}}
    >
      <div className="absolute inset-0 bg-cover bg-center" onClick={tapPhoto}
        style={{ backgroundImage: `url(${photoSrc(photos[photoIdx]?.url)})`, backgroundColor: "#15121E" }}>
      </div>

      {photos.length > 1 && (
        <div className="absolute top-3 left-3 right-3 flex gap-1.5 z-20">
          {photos.map((_, i) => (
            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i === photoIdx ? "bg-white" : "bg-white/30"}`} />
          ))}
        </div>
      )}

      {profile.is_bot && (
        <div data-testid="bot-badge" className="absolute top-7 left-3 z-30 px-2 py-0.5 rounded-md bg-amber-400 text-black text-[10px] font-extrabold tracking-wide shadow-lg">
          BOT
        </div>
      )}

      {drag && (
        <>
          <motion.div style={{ opacity: likeOpacity }} className="absolute top-10 left-6 z-30 -rotate-12 border-4 border-emerald-400 text-emerald-400 px-4 py-1 rounded-xl text-2xl font-black tracking-wider">
            LIKE
          </motion.div>
          <motion.div style={{ opacity: nopeOpacity }} className="absolute top-10 right-6 z-30 rotate-12 border-4 border-rose-500 text-rose-500 px-4 py-1 rounded-xl text-2xl font-black tracking-wider">
            NOPE
          </motion.div>
        </>
      )}

      <div className="absolute inset-x-0 bottom-0 p-5 pt-20 z-10 bg-gradient-to-t from-black/95 via-black/55 to-transparent pointer-events-none">
        <div className="flex items-end justify-between gap-2 pointer-events-auto">
          <div>
            <h2 className="text-3xl font-extrabold text-white drop-shadow-lg" style={{ fontFamily: "Outfit, sans-serif" }}>
              {profile.name}<span className="font-semibold text-white/90">, {profile.age}</span>
            </h2>
            <div className="flex items-center gap-1.5 text-white/80 text-sm mt-1">
              <MapPin className="w-4 h-4" />
              <span>
                {profile.neighborhood ? `${profile.neighborhood}, ` : ""}Kalaburagi
                {profile.distance_km != null && <span className="text-rose-300 font-medium"> · {profile.distance_km} km away</span>}
              </span>
            </div>
          </div>
          <button data-testid="swipe-info-button" onClick={(e) => { e.stopPropagation(); setShowBio((s) => !s); }}
            className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-md grid place-items-center text-white active:scale-90 transition-transform">
            <Info className="w-5 h-5" />
          </button>
        </div>

        {showBio && (
          <div className="mt-3 animate-float-up pointer-events-auto">
            {profile.bio && <p className="text-white/85 text-sm leading-relaxed">{profile.bio}</p>}
            {profile.interests?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {profile.interests.map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full bg-rose-500/20 border border-rose-400/30 text-rose-200 text-xs font-medium">{t}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}
